import { type Profile, type Print, type User, type PendingUser } from 'app/store/types';
import { objectKeys } from 'ts-extras';
import { isValidSlug } from './helpers';

const MANAGED_PRINT_GROUP_TYPES_LIST = ['shared', 'guest', 'direct', 'r2mIn', 'system'] as const;
const PRINT_GROUP_TYPES_LIST = ['personal', 'msUniversal', ...MANAGED_PRINT_GROUP_TYPES_LIST] as const;
const PRINT_GROUP_TYPES_AND_DELEGATED_LIST = [
	'personal',
	'delegated',
	'msUniversal',
	...MANAGED_PRINT_GROUP_TYPES_LIST,
] as const;

export const managedPrintGroupTypes = (pageMode: 'direct' | 'r2m' | 'all' = 'all') =>
	MANAGED_PRINT_GROUP_TYPES_LIST.filter((printGroupType) =>
		pageMode === 'direct' ? printGroupType === 'direct' : pageMode === 'r2m' ? printGroupType !== 'direct' : true,
	);
export const printGroupTypes = (pageMode: 'direct' | 'r2m' | 'all' = 'all') =>
	PRINT_GROUP_TYPES_LIST.filter((printGroupType) =>
		pageMode === 'direct' ? printGroupType === 'direct' : pageMode === 'r2m' ? printGroupType !== 'direct' : true,
	);
export const printGroupTypesAndDelegated = (pageMode: 'direct' | 'r2m' | 'all' = 'all') =>
	PRINT_GROUP_TYPES_AND_DELEGATED_LIST.filter((printGroupType) =>
		pageMode === 'direct' ? printGroupType === 'direct' : pageMode === 'r2m' ? printGroupType !== 'direct' : true,
	);

export const isDelegatedToMePrint = (print: Print, profileId: Profile['id']) => {
	const delegateUserIds = print.acl.users.filter(({ role }) => role !== 'owner').map(({ name }) => name);
	return delegateUserIds.includes(profileId);
};

export const printPreferencesMenuOptions = {
	color: ['3', '1'],
	collate: ['False', 'True'],
	offset: ['False', 'True'],
	layout: ['NORMAL', 'DUPLEX', 'BOOKLET4IN1'],
	binding: ['LEFT', 'RIGHT', 'TOP'],
	staple: ['', 'CORNER', 'ANY2POINT'],
	fold: ['', 'CENTERFOLDIN'],
	punch: ['', '2', '3'],
} as const;

// this is coincidentally the same list as `printPreferencesMenuOptions` because the only other option on the screen is `copies` and you can't disable editable for that
const printPreferencesEditableNames = [...objectKeys(printPreferencesMenuOptions)] as const;

type PrintPreferenceEditableName = (typeof printPreferencesEditableNames)[number];

export const isEditablePrintPreference = (
	printPreferenceName: keyof PrintPreferences,
): printPreferenceName is PrintPreferenceEditableName => {
	return printPreferencesEditableNames.some((name) => name === printPreferenceName);
};

export type Option = {
	name: string;
	value: string;
	selectable: boolean;
	displayName: string;
	active: boolean;
	defaultEnabled: boolean;
};

export type PrintPreferences = {
	guestDuration: number;
	printJobExpirationDuration: number;
	guestQueueExpirationDuration: number;
	copies: number;
	headers: Option[];
	logoBase64?: string;
	logoS3Key?: string;
	systemPrintQueue?: {
		useStratusDefault: boolean;
		designatedEmail?: string;
		emailSubject?: string;
		emailBodyText?: string;
		fromEmailText?: string;
		logoUrl?: string;
		logoKey?: string;
		blockedGuestEmails: string[];
	};
} & {
	[key in keyof typeof printPreferencesMenuOptions]: (typeof printPreferencesMenuOptions)[key][number];
} & {
	[key in PrintPreferenceEditableName as `${key}_editable`]: boolean;
};

export const mockPrintPreferences = {
	guestDuration: 7 * 24 * 60 * 60,
	printJobExpirationDuration: 7 * 24 * 60 * 60,
	guestQueueExpirationDuration: 7 * 24 * 60 * 60,
	copies: 1,
	headers: [] as PrintPreferences['headers'],
	...Object.fromEntries(Object.entries(printPreferencesMenuOptions).map(([key, value]) => [key, value[0]])),
	...Object.fromEntries(printPreferencesEditableNames.map((name) => [`${name}_editable`, true])),
} as PrintPreferences;

export const editablePrintPreferenceIsDisabled = (preferences: PrintPreferences) => (name: keyof PrintPreferences) =>
	isEditablePrintPreference(name) && preferences[`${name}_editable`] === false;

export const validPrintPreferences = (preferences: PrintPreferences) => {
	const hasEmptyHeaderName = preferences.headers.some((header) => header.name.trim() === '');

	if (hasEmptyHeaderName) {
		return false;
	}

	// Validate system print queue fields
	if (preferences.systemPrintQueue && !preferences.systemPrintQueue.useStratusDefault) {
		const slug = preferences.systemPrintQueue.designatedEmail;
		if (slug && !isValidSlug(slug)) {
			return false;
		}
	}

	return true;
};

export type NewGuestUserData = { email: string; authentication: string[]; expirationDuration: number };
export type GuestUserData = NewGuestUserData & { id: string };

export const isGuestUserData = (userData: NewGuestUserData | GuestUserData): userData is GuestUserData => {
	return 'id' in userData;
};
